import { defineStore } from 'pinia';
import { useUnsavedChangesDetection } from '@/composables/useUnsavedChangesDetection';
import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
import { extractChartStyleObject } from '@/composables/extractChartStyleObject';
import { INVALID_CHART_RECID } from '@/composables/isValidChartRecid';
import { INVALID_CHART_STYLE_RECID, isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
import { getChartTypeObjFromChartTypeValue } from '@/enums/chart-type-options';
import { COLOR_SCHEMES_ARRAY } from '@/enums/color-schemes';
import { UP_DOWN_COLOR_OPTION_AUTO } from '@/enums/up-down-color-options';
import { ICHIMOKU_CLOUD, ICHIMOKU_CLOUD_FULL } from '@/enums/indicators-and-overlays';
import { RANGE_OPTIONS_ARRAY, RANGE_OPTION_PREDEFINED_RANGE, RANGE_OPTION_YEAR_TO_DATE, getRangeObjFromRangeTitle } from '@/enums/range-options';
import { getPeriodOptionFromPeriodValue } from '@/enums/period-options';
import { Overlay, Chart, ChartStyle } from '@/interfaces/chart';
import { ChartStore } from '@/types/ChartStore';

function stateFn(): ChartStore {
  const { getPlatformDefaultChart } = useLoadChartsAndChartStyles();

  return {
    recidOfChartBeingViewed: INVALID_CHART_RECID, // This could be the recid of a chart belonging to a different user. This should NOT be the recid of a point-and-figure chart.
    recidOfLoadSymbolOnlyChartBeingViewed: INVALID_CHART_RECID, // This could be the recid of a chart belonging to a different user. This could be the recid of a point-and-figure chart.
    recidOfChartStyleBeingViewed: INVALID_CHART_STYLE_RECID,
    latestLoadChartTimeStamp: -1,
    latestLoadBrowserTabHistoryChartTimeStamp: -1,
    latestLoadUrlStyleCacheIdParameterChartTimeStamp: -1,
    latestLoadConvertedPerfChartTimeStamp: -1,
    latestLoadPerfChartTimeStamp: -1,
    latestLoadChartStyleTimeStamp: -1,
    latestChangeColorSchemeTimeStamp: -1,
    isMakingEntireStartEndDateChange: false,
    ...getPlatformDefaultChart()
  };
}

const useChartStore = defineStore('chartStore', {
  state: stateFn,
  getters: {
    chart: function (state): Chart {
      return {
        ...extractChartStyleObject(state),
        symbol: state.symbol
      };
    },
    chartStyle: function (state): ChartStyle {
      return extractChartStyleObject(state);
    },
    periodTitle: function (state) {
      return getPeriodOptionFromPeriodValue(state.period).title;
    },
    periodShortTitle: function (state) {
      return getPeriodOptionFromPeriodValue(state.period).shortTitle;
    },
    rangeShortTitle: function (state) {
      const selectedRangeOption = getRangeObjFromRangeTitle(state.range);

      if (selectedRangeOption.useBarAndGap) {
        return selectedRangeOption.shortTitle;
      }

      if (selectedRangeOption.useNumberOfDays) {
        for (const rangeOption of RANGE_OPTIONS_ARRAY) {
          if (
            !rangeOption.useNumberOfDays ||
            rangeOption === RANGE_OPTION_PREDEFINED_RANGE ||
            rangeOption.numberOfDays !== state.rangeDays ||
            rangeOption.numberOfMonths !== state.rangeMonths ||
            rangeOption.numberOfYears !== state.rangeYears
          ) {
            continue;
          }

          return rangeOption.shortTitle;
        }
      }

      return selectedRangeOption.shortTitle;
    },
    chartTypeShortTitle: function (state) {
      return getChartTypeObjFromChartTypeValue(state.chartType).shortTitle;
    },
    ichimokuCloudOverlays: function (state) {
      const ichimokuCloudOverlays: Overlay[] = [];

      for (const overlay of state.sortedOverlays) {
        if (
          overlay &&
          (overlay.overlayName === ICHIMOKU_CLOUD.name || overlay.overlayName === ICHIMOKU_CLOUD_FULL.name)
        ) {
          ichimokuCloudOverlays.push(overlay);
        }
      }

      return ichimokuCloudOverlays;
    },
    urlEncodedSymbol: function (state) {
      return encodeURIComponent(state.symbol);
    },
    isChartUnadjustedForDividends: function (state) {
      return state.symbol.startsWith('_') || state.symbol.startsWith('-_');
    },
    isMainPriceScaleInverted: function (state) {
      return state.symbol.startsWith('-') || state.symbol.startsWith('_-');
    },
    shouldDisplayRangeBarAndGapMenus: function (state) {
      return getRangeObjFromRangeTitle(state.range).useBarAndGap;
    },
    shouldDisplayRangeYearMonthAndDayMenus: function (state) {
      return getRangeObjFromRangeTitle(state.range).useNumberOfDays;
    },
    shouldDisplayRangeDatePickers: function (state) {
      return getRangeObjFromRangeTitle(state.range).useStartDateAndEndDate;
    },
    upDownColorsArray: function (state) {
      const currentColorScheme = COLOR_SCHEMES_ARRAY.find(colorScheme => colorScheme.value === state.colorScheme);

      if (!currentColorScheme) {
        return [UP_DOWN_COLOR_OPTION_AUTO];
      }

      return [
        UP_DOWN_COLOR_OPTION_AUTO,
        ...currentColorScheme.sharpCharts2OverlaySwatches.map(swatch => swatch.upDownColorOption)
      ];
    },
    recidOfChartStyleBeingViewedWithAllChangesSaved: function (state): number {
      const { doesChartStyleBeingViewedHaveUnsavedChanges } = useUnsavedChangesDetection();

      if (
        !isValidChartStyleRecid(state.recidOfChartStyleBeingViewed, true) ||
        doesChartStyleBeingViewedHaveUnsavedChanges.value
      ) {
        return INVALID_CHART_STYLE_RECID;
      }

      return state.recidOfChartStyleBeingViewed;
    }
  },
  actions: {}
});

export {
  useChartStore
};
